import { useDispatch } from "react-redux";

import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dataFormatter } from "src/api/axiosCalls";

import { organizationQueryKeyFactory } from "@features/organizations/data/organizationQueries";
import { AuthUser } from "@models/AuthUser";
import { getUserSuccess } from "@redux/slices/user/currentUserSlice";
import { mapOrganization, mapUser } from "@redux/slices/user/maps";
import { Dispatch } from "@reduxjs/toolkit";
import client from "@services/api";
import { QueryOptions } from "@utils/reactQuery";

export const currentUserKeyFactory = createQueryKeys("current-user", {
  detail: {
    queryKey: null,
  },
});

export const handleUserResponse = (res: any, dispatch: Dispatch) => {
  // Until we get the entire app state to use react-query user state, we must also keep the redux user state up-to-date
  // For simplicty and backwards compatibility, we deserialize the user data differently for redux
  const reduxUserPayload = mapUser(dataFormatter.deserialize(res));
  const reduxOrganizationPayload = mapOrganization(
    (dataFormatter.deserialize(res) as any).organization
  );
  dispatch(
    getUserSuccess({
      user: reduxUserPayload,
      organization: reduxOrganizationPayload,
    })
  );

  const deserialized = client.deserialize(res);

  return deserialized.data as AuthUser;
};

export const useCurrentUserQuery = (queryOptions?: QueryOptions<AuthUser>) => {
  const dispatch = useDispatch();
  return useQuery({
    queryKey: currentUserKeyFactory.detail.queryKey,
    queryFn: () =>
      client
        .get<AuthUser>("current-user", { deserializeOutput: false })
        .then((res) => handleUserResponse(res, dispatch)),
    staleTime: 2 * 60 * 60_000,
    ...queryOptions,
  });
};

export const useChangeOrganizationMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return useMutation({
    mutationFn: (organizationId: string) => {
      return client.post<AuthUser>(
        "current-user/update-organization",
        {
          data: {
            "organization-id": +organizationId,
          },
        },
        { serializeBody: false, deserializeOutput: false }
      );
    },
    onSettled: async (res) => {
      // clear query client cache since data is scoped by organization
      queryClient.removeQueries({
        predicate: (q) =>
          q.queryKey[0] !== currentUserKeyFactory._def[0] &&
          q.queryKey[0] !== organizationQueryKeyFactory._def[0],
      });
      const authUser = handleUserResponse(res, dispatch);
      await queryClient.setQueryData(
        currentUserKeyFactory.detail.queryKey,
        () => authUser
      );
    },
  });
};
